import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuthStore } from '../store/adminAuthStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import NavBar from '../components/NavBar';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const navigate = useNavigate();
  const { signIn, changePassword, isLoading, error, clearError, requiresPasswordChange, isAuthenticated } = useAdminAuthStore();
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !requiresPasswordChange) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, requiresPasswordChange, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      await signIn(email, password);
      // If password change is required, the store will set requiresPasswordChange
      // and we'll show the password change form
      if (!requiresPasswordChange) {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      // Error is handled by the store
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (newPassword !== confirmPassword) {
      // We'll set error in store
      return;
    }

    if (newPassword.length < 8) {
      return;
    }

    try {
      await changePassword(newPassword);
      navigate('/admin', { replace: true });
    } catch (err) {
      // Error is handled by the store
    }
  };

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} flex flex-col relative`}>
      <NavBar className={`${tokens.card.bg} shadow-sm`} containerClassName="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Runow</h1>
      </NavBar>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-3">
        <div className={`max-w-md w-full ${tokens.card.bg} rounded-landing ${tokens.card.shadow} p-6`}>
          <h1 className={`text-2xl font-bold ${tokens.text.primary} mb-2 text-center`}>
            Admin Login
          </h1>
          <p className={`text-sm ${tokens.text.muted} mb-6 text-center`}>
            Sign in with your admin account
          </p>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-sm p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {requiresPasswordChange ? (
            <div>
              <p className={`text-sm ${tokens.text.primary} mb-4 text-center`}>
                You must change your password before continuing.
              </p>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className={`w-full px-3 py-2 pr-12 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-sm ${tokens.text.muted} hover:${tokens.text.primary} focus:outline-none transition-colors rounded-sm min-h-[48px] flex items-center justify-center font-medium`}
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                    >
                      {showNewPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <p className={`text-xs ${tokens.text.muted} mt-1`}>
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                    placeholder="••••••••"
                  />
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading || newPassword !== confirmPassword || newPassword.length < 8}
                  className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-2 px-4 transition-colors min-h-[48px]`}
                >
                  {isLoading ? 'Changing password...' : 'Change Password'}
                </button>
              </form>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className={`block text-xs font-medium ${tokens.text.primary} mb-1`}>
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`w-full px-3 py-2 pr-12 ${tokens.border.light} border rounded-landing focus:ring-2 ${tokens.border.accent} focus:border-transparent ${tokens.card.bg} ${tokens.text.primary}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-sm ${tokens.text.muted} hover:${tokens.text.primary} focus:outline-none transition-colors rounded-sm min-h-[48px] flex items-center justify-center font-medium`}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${tokens.button.primary} disabled:opacity-50 disabled:cursor-not-allowed font-semibold py-2 px-4 transition-colors min-h-[48px]`}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
          )}

          <div className="mt-6 text-center">
            <p className={`text-sm ${tokens.text.muted}`}>
              <button
                onClick={() => navigate('/login')}
                className={`${tokens.text.accent} hover:opacity-80 font-medium transition-opacity`}
              >
                ← Back to regular login
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
